import React, { useState } from "react";
import PropTypes from "prop-types";
import {
    Form as FormikForm,
    AutocompleteField,
    InputField,
    CheckboxField,
    TimePickerField,
    TextareaField,
} from "components/common_components/Formik";
import * as Yup from "yup";
import Grid from "@material-ui/core/Grid";
import Button from "components/common_components/Button";
import Card from "components/common_components/Card";
import Icon from "components/common_components/Icon";
import UserOption from "components/common_components/UserSelect/UserOption";
import { DataSearchDialog } from "components/common_components/Dialogs";
import Autocomplete from "components/common_components/Autocomplete/Autocomplete";
import { getDataNormalizer, getFiltersBuilder } from "components/Modules/Forms";
import { Radio } from "components/common_components/Radio";
import { hasElements } from "utils/arrays";

const CHECK_IN_TYPE_OPTIONS = [
    { value: "client", label: "Client" },
    { value: "visitor", label: "Visitor" },
];

function Form({ signinSheet, resources, mode, onEdit, modulesApi, allowedModules, visitorsApi, ...rest }) {
    const readOnly = mode === "detail";
    const { users } = resources;
    const [checkInType, setCheckInType] = useState(signinSheet.visitorId ? "visitor" : "client");
    const [selectedModule, setSelectedModule] = useState(null);

    const moduleOptions = (allowedModules || []).filter((m) => m.dbName && m.formName);

    // --- Case Search Dialog (Client flow) ---

    const caseSearchHeadCells = [
        { index: 0, id: "fileNo", disablePadding: true, label: "File #" },
        { index: 1, id: "client.fullName", label: "Client", sortable: false },
        { index: 2, id: "type.type", disablePadding: true, label: "Type" },
        { index: 3, id: "assigneeUser.name", label: "Assignee", sortable: false },
    ];

    const [searchCaseDialogProps, setSearchCaseDialogProps] = useState({
        open: false,
        title: "Search Case",
        onClose: () => setSearchCaseDialogProps((prev) => ({ ...prev, open: false })),
        onPrimaryClick: undefined,
        tableProps: {
            headCells: caseSearchHeadCells,
            defaultOrder: { order: "desc", orderBy: "id" },
            keyColumnName: "id",
            selectable: true,
            allowSelectAll: false,
            allowMultiSelect: false,
            dense: false,
            deletable: false,
            onRequestData: async () => ({ data: [], pageableData: undefined }),
            filterBuilder: () => ({}),
        },
    });

    function setCaseFromDialog(selectedItems, form) {
        const selectedCase = selectedItems[0];
        if (selectedCase) {
            form.setFieldValue("caseFileNo", selectedCase.fileNo || "");
            if (selectedCase.client) {
                form.setFieldValue("firstName", selectedCase.client.firstName || "");
                form.setFieldValue("middleName", selectedCase.client.middleName || "");
                form.setFieldValue("lastName", selectedCase.client.lastName || "");
            }
            if (selectedCase.assigneeUser) {
                form.setFieldValue("assignee", selectedCase.assigneeUser);
            }
        }
    }

    function searchCase(form) {
        if (!selectedModule) return;
        const DB = modulesApi(selectedModule.dbName);
        const normalizer = getDataNormalizer(selectedModule.formName);
        const filterBuilder = getFiltersBuilder(selectedModule.formName);

        setSearchCaseDialogProps((prev) => ({
            ...prev,
            open: true,
            onPrimaryClick: (selectedItems) => setCaseFromDialog(selectedItems, form),
            tableProps: {
                ...prev.tableProps,
                headCells: caseSearchHeadCells,
                onRequestData: (data) => DB.PersonalInfo.getListAsync(data, normalizer),
                filterBuilder,
            },
        }));
    }

    // --- Visitor Search Dialog (Visitor flow) ---

    function filterVisitorsBuilder(filterValue) {
        return filterValue
            ? {
                  firstName: filterValue,
                  lastName: filterValue,
                  homePhone: filterValue,
              }
            : {};
    }

    const [searchVisitorDialogProps, setSearchVisitorDialogProps] = useState({
        open: false,
        title: "Search Visitor",
        onClose: () => setSearchVisitorDialogProps((prev) => ({ ...prev, open: false })),
        onPrimaryClick: undefined,
        tableProps: {
            defaultOrder: { order: "desc", orderBy: "visitDate" },
            keyColumnName: "id",
            selectable: true,
            allowSelectAll: false,
            allowMultiSelect: false,
            dense: false,
            deletable: false,
            onRequestData: visitorsApi ? visitorsApi.getListAsync : async () => ({ data: [], pageableData: undefined }),
            filterBuilder: filterVisitorsBuilder,
        },
    });

    function setVisitorFromDialog(selectedItems, form) {
        const selectedVisitor = selectedItems[0];
        if (selectedVisitor) {
            form.setFieldValue("visitorId", selectedVisitor.id);
            form.setFieldValue("firstName", selectedVisitor.firstName || "");
            form.setFieldValue("middleName", selectedVisitor.middleName || "");
            form.setFieldValue("lastName", selectedVisitor.lastName || "");
        }
    }

    function searchVisitor(form) {
        const headCells = [
            { index: 1, id: "firstName", label: "First Name" },
            { index: 2, id: "middleName", label: "Middle Name" },
            { index: 3, id: "lastName", label: "Last Name" },
            { index: 4, id: "visitDateString", orderBy: "visitDate", label: "Visit Date" },
        ];
        setSearchVisitorDialogProps((prev) => ({
            ...prev,
            open: true,
            onPrimaryClick: (selectedItems) => setVisitorFromDialog(selectedItems, form),
            tableProps: {
                headCells,
                ...prev.tableProps,
            },
        }));
    }

    const activeUsers = hasElements(users) ? users.filter((u) => u.isActive) : [];

    // --- Validation ---

    const validationSchema = Yup.object({
        firstName: Yup.string().required("Required field").max(50, "Maximum 50 characters"),
        middleName: Yup.string().max(50, "Maximum 50 characters"),
        lastName: Yup.string().required("Required field").max(50, "Maximum 50 characters"),
        assignee: Yup.object()
            .nullable()
            .required("Required field")
            .shape({
                id: Yup.number().required(),
                name: Yup.string().required(),
                email: Yup.string().required(),
            })
            .test("is-active", "The selected user must be active", (value) => !value || value.isActive === true),
        isFirstTime: Yup.boolean(),
        caseFileNo: Yup.string().nullable().max(50, "Maximum 50 characters"),
        appointmentTime: Yup.date().nullable(),
        reasonForVisit: Yup.string().max(500, "Maximum 500 characters"),
    });

    return (
        <>
            <FormikForm
                validateOnChange={false}
                initialValues={signinSheet}
                validationSchema={validationSchema}
                showButtons={false}
                {...rest}
            >
                {(form) => (
                    <>
                        <Grid container spacing={3} justifyContent="center">
                            <Grid item sm={12} md={12} lg={10}>
                                <Card title="Check-in for">
                                    <Grid container spacing={2} alignItems="center">
                                        <Grid item xs={12} md={4}>
                                            <Radio
                                                options={CHECK_IN_TYPE_OPTIONS}
                                                value={checkInType}
                                                onChange={setCheckInType}
                                                label=""
                                                disabled={readOnly}
                                            />
                                        </Grid>
                                    </Grid>

                                    {!readOnly && (
                                        <Grid container spacing={2} alignItems="center">
                                            {checkInType === "client" && (
                                                <>
                                                    <Grid item xs={12} md={3}>
                                                        <Autocomplete
                                                            value={selectedModule}
                                                            options={moduleOptions}
                                                            valueKey="code"
                                                            labelKey="name"
                                                            onChange={setSelectedModule}
                                                            label="Module"
                                                            size="small"
                                                            margin="dense"
                                                            getOptionLabel={(option) => (option ? option.name : "")}
                                                            getOptionSelected={(opt, val) => opt.code === val.code}
                                                        />
                                                    </Grid>
                                                    <Grid item xs={12} md={2}>
                                                        <Button secondary disabled={!selectedModule} onClick={() => searchCase(form)}>
                                                            <Icon name="search" size="xs" color="white" />
                                                            <span>Search Case</span>
                                                        </Button>
                                                    </Grid>
                                                </>
                                            )}
                                            {checkInType === "visitor" && (
                                                <Grid item xs={12} md={3}>
                                                    <Button secondary onClick={() => searchVisitor(form)}>
                                                        <Icon name="search" size="xs" color="white" />
                                                        <span>Search Visitor</span>
                                                    </Button>
                                                </Grid>
                                            )}
                                        </Grid>
                                    )}
                                    {checkInType === "client" && (
                                        <Grid container spacing={2} alignItems="center">
                                            <Grid item xs={12} md={2}>
                                                <InputField
                                                    disabled
                                                    fieldName="caseFileNo"
                                                    label="Case"
                                                    inputProps={{ style: { textTransform: "uppercase" } }}
                                                />
                                            </Grid>
                                        </Grid>
                                    )}
                                </Card>
                            </Grid>
                        </Grid>

                        <Grid container spacing={3} justifyContent="center">
                            <Grid item sm={12} md={12} lg={10}>
                                <Card title="Personal Information">
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} md={4}>
                                            <InputField disabled={readOnly} fieldName="firstName" label="First Name *" />
                                        </Grid>
                                        <Grid item xs={12} md={4}>
                                            <InputField disabled={readOnly} fieldName="middleName" label="Middle Name" />
                                        </Grid>
                                        <Grid item xs={12} md={4}>
                                            <InputField disabled={readOnly} fieldName="lastName" label="Last Name *" />
                                        </Grid>
                                    </Grid>
                                </Card>
                            </Grid>
                        </Grid>
                        <Grid container spacing={3} justifyContent="center">
                            <Grid item sm={12} md={12} lg={10}>
                                <Card title="Visit Details">
                                    <Grid container spacing={2} alignItems="center">
                                        <Grid item xs={12} md={2}>
                                            <CheckboxField disabled={readOnly} fieldName="isFirstTime" label="First Time?" />
                                        </Grid>
                                        <Grid item xs={12} md={4}>
                                            <AutocompleteField
                                                disabled={readOnly}
                                                fieldName="assignee"
                                                label="Assignee *"
                                                options={activeUsers}
                                                valueKey="id"
                                                labelKey="name"
                                                renderOption={(user) => <UserOption {...user} />}
                                                getOptionLabel={(option) => (option ? option.name : "")}
                                                getOptionSelected={(opt, val) => opt.id === val.id}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={4}>
                                            <TimePickerField
                                                disabled={readOnly}
                                                fieldName="appointmentTime"
                                                label="Appointment Time"
                                                ampm={false}
                                                clearable
                                            />
                                        </Grid>
                                    </Grid>
                                </Card>
                            </Grid>
                        </Grid>
                        <Grid container spacing={3} justifyContent="center">
                            <Grid item sm={12} md={12} lg={10}>
                                <Card title="Additional Information">
                                    <Grid container spacing={2}>
                                        <Grid item xs={12}>
                                            <TextareaField
                                                disabled={readOnly}
                                                fieldName="reasonForVisit"
                                                label="Reason for Visit"
                                                fullWidth
                                                maxLength={500}
                                                rows={3}
                                            />
                                        </Grid>
                                    </Grid>
                                </Card>
                            </Grid>
                        </Grid>
                        {readOnly ? (
                            <Grid container spacing={3} justifyContent="center">
                                <Grid item sm={12} md={12} lg={10}>
                                    <div className="button-group">
                                        <Button secondary outline onClick={rest.onCancel}>
                                            <Icon name="arrow" size="md" color="secondary" />
                                            <span>Back</span>
                                        </Button>
                                        <Button secondary onClick={onEdit}>
                                            <Icon name="pencil" size="md" color="white" />
                                            <span>Edit</span>
                                        </Button>
                                    </div>
                                </Grid>
                            </Grid>
                        ) : (
                            <Grid container spacing={3} justifyContent="center">
                                <Grid item sm={12} md={12} lg={10}>
                                    <div className="button-group">
                                        <Button
                                            outline
                                            onClick={() => {
                                                form.resetForm();
                                                rest.onCancel(form);
                                            }}
                                        >
                                            Cancel
                                        </Button>
                                        <Button primary type="submit" loading={form.isSubmitting}>
                                            <Icon name="save" size="md" color="white" />
                                            <span>Save</span>
                                        </Button>
                                    </div>
                                </Grid>
                            </Grid>
                        )}
                    </>
                )}
            </FormikForm>
            <DataSearchDialog {...searchCaseDialogProps} />
            <DataSearchDialog {...searchVisitorDialogProps} />
        </>
    );
}

Form.propTypes = {
    signinSheet: PropTypes.shape({
        firstName: PropTypes.string,
        middleName: PropTypes.string,
        lastName: PropTypes.string,
        assignee: PropTypes.shape({
            id: PropTypes.number,
            name: PropTypes.string,
            email: PropTypes.string,
        }),
        isFirstTime: PropTypes.bool,
        caseFileNo: PropTypes.string,
        appointmentTime: PropTypes.object,
        reasonForVisit: PropTypes.string,
    }),
    resources: PropTypes.shape({
        users: PropTypes.arrayOf(
            PropTypes.shape({
                id: PropTypes.number,
                name: PropTypes.string,
                email: PropTypes.string,
            }),
        ),
    }),
    mode: PropTypes.oneOf(["add", "detail", "update"]).isRequired,
    onEdit: PropTypes.func,
};

Form.defaultProps = {
    signinSheet: {
        firstName: "",
        middleName: "",
        lastName: "",
        assignee: null,
        isFirstTime: false,
        caseFileNo: "",
        appointmentTime: null,
        reasonForVisit: "",
        visitorId: null,
    },
    resources: {
        users: [],
    },
    onEdit: () => {},
};

export default Form;
